import { useRef, useState, useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useCollectionBinding,
  useProperty,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Slot group definitions ───────────────────────────────────────────────────

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface FloorplanGroup {
  floorplan_group_id?: string;
  floorplan_group_name?: string;
  floorplan_group_url?: string;
}

interface Floorplan {
  floorplan_id?: string;
  floorplan_beds_min?: string;
}

// ─── Bedroom-label helper (fallback when floorplan_groups is empty) ──────────

const BED_LABELS: Record<string, string> = {
  '0': 'Studio',
  '1': 'One Bedroom',
  '2': 'Two Bedroom',
  '3': 'Three Bedroom',
  '4': 'Four Bedroom',
  '5': 'Five Bedroom',
};

const BED_ORDER = [
  'Studio',
  'One Bedroom',
  'Two Bedroom',
  'Three Bedroom',
  'Four Bedroom',
  'Five Bedroom',
];

function bedLabel(bedsMin: string | undefined): string {
  if (!bedsMin) return '';
  const key = bedsMin.trim().toLowerCase();
  if (key === 'studio' || key === 'std') return 'Studio';
  return BED_LABELS[key] ?? `${bedsMin} Bedroom`;
}

// Normalize a bed count or a label ("One Bedroom", "1 Bed", "Studio") to the
// canonical `beds` query value the ApartmentSync widget filter understands
// ('studio' or a digit string). Returns null when no count can be derived.
const BED_WORDS: Record<string, string> = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
};

function canonicalBeds(input: string | undefined): string | null {
  if (!input) return null;
  const t = input.trim().toLowerCase();
  if (!t) return null;
  if (t.includes('studio') || t === 'std') return 'studio';
  // Prefer a digit that directly qualifies bedrooms ("1 bed", "2 br",
  // "3-bedroom") so unrelated numbers (bath count, sqft) can't hijack the value.
  const bedDigit = t.match(/(\d+)\s*-?\s*(?:bed|bedroom|br\b)/);
  if (bedDigit) return bedDigit[1];
  // Spelled-out bedroom count ("one bedroom").
  for (const [word, n] of Object.entries(BED_WORDS)) {
    if (new RegExp(`\\b${word}\\b`).test(t)) return n;
  }
  // Last resort: a lone digit, only when no bedroom context was found above.
  const digit = t.match(/\d+/);
  if (digit) return digit[0];
  return null;
}

// Link to the floor-plans page, carrying the selected bedroom count so the
// ApartmentSync widget there can pre-filter to that count.
function floorPlansHref(beds: string | null): string {
  return beds ? `/floor-plans?bedroom=${encodeURIComponent(beds)}` : '/floor-plans';
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const property = useProperty();

  const featuredVideoRaw = (property as Record<string, unknown>)[
    'custom_property_featured_video'
  ] as string | null | undefined;

  // Primary bedroom-button source — floorplan_groups
  const bedroomGroups = useCollectionBinding<FloorplanGroup>('hero.bedroom_groups', {
    collection: 'floorplan_groups',
    defaultLimit: 6,
  });

  // Fallback source — derive unique bed counts from floorplans collection
  const floorplans = useCollectionBinding<Floorplan>('hero.floorplans', {
    collection: 'floorplans',
    defaultLimit: 50,
  });

  const derivedBedButtons = useMemo(() => {
    if (bedroomGroups.length > 0) return [];
    const seen = new Set<string>();
    const buttons: { label: string; href: string }[] = [];
    for (const fp of floorplans) {
      const key = fp.floorplan_beds_min?.trim() ?? '';
      if (!key || seen.has(key)) continue;
      const label = bedLabel(key);
      if (!label) continue;
      seen.add(key);
      buttons.push({ label, href: floorPlansHref(canonicalBeds(key)) });
    }
    buttons.sort((a, b) => {
      const ai = BED_ORDER.indexOf(a.label);
      const bi = BED_ORDER.indexOf(b.label);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return buttons;
  }, [bedroomGroups.length, floorplans]);

  // ─── Video setup ────────────────────────────────────────────────────────────
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const hasVideo = !!(
    featuredVideoRaw &&
    typeof featuredVideoRaw === 'string' &&
    featuredVideoRaw.trim()
  );

  const videoInfo = useMemo(() => {
    if (!hasVideo) return null;
    const url = featuredVideoRaw!.trim();

    if (url.includes('vimeo.com')) {
      const videoIdMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      const videoId = videoIdMatch?.[1];
      if (!videoId) return { type: 'embed' as const, src: url };
      return {
        type: 'embed' as const,
        src: `https://player.vimeo.com/video/${videoId}?autoplay=1&loop=1&title=0&byline=0&portrait=0&background=1&muted=1&autopause=0`,
      };
    }

    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId: string | null = null;
      const embedMatch = url.match(/embed\/([^?&/]+)/);
      const watchMatch = url.match(/[?&]v=([^&]+)/);
      const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
      videoId = embedMatch?.[1] || watchMatch?.[1] || shortMatch?.[1] || null;
      if (!videoId) return { type: 'embed' as const, src: url };
      return {
        type: 'embed' as const,
        src: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1`,
      };
    }

    return { type: 'direct' as const, src: url };
  }, [hasVideo, featuredVideoRaw]);

  useEffect(() => {
    if (!videoInfo || videoInfo.type !== 'direct') return;
    const video = videoRef.current;
    if (!video) return;
    const onCanPlay = () => setVideoReady(true);
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 3) setVideoReady(true);
    return () => video.removeEventListener('canplay', onCanPlay);
  }, [videoInfo]);

  return (
    <section className="relative w-full h-[65vh] md:h-[70vh] min-h-[420px] max-h-[780px] overflow-hidden">
      {/* Background — video if available, otherwise static image */}
      {videoInfo ? (
        <div className="absolute inset-0">
          {bgImage.image && (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                videoInfo.type === 'embed'
                  ? iframeLoaded
                    ? 'opacity-0'
                    : 'opacity-100'
                  : videoReady
                    ? 'opacity-0'
                    : 'opacity-100'
              }`}
            />
          )}
          {videoInfo.type === 'embed' ? (
            <iframe
              src={videoInfo.src}
              className={`hero-video-iframe absolute inset-0 w-full h-full transition-opacity duration-1000 ${iframeLoaded ? 'opacity-100' : 'opacity-0'}`}
              allow="autoplay; fullscreen"
              frameBorder="0"
              onLoad={() => setIframeLoaded(true)}
              title="Background video"
            />
          ) : (
            <video
              ref={videoRef}
              src={videoInfo.src}
              autoPlay
              muted
              loop
              playsInline
              className={`w-full h-full object-cover transition-opacity duration-1000 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
            />
          )}
        </div>
      ) : (
        <EditableSlotGroup
          groupPath="hero.bg_image"
          definition={featuredImageDef}
          className="absolute inset-0"
        >
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              priority
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </EditableSlotGroup>
      )}

      {/* Dark gradient — deeper overall so white text stays readable */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/60 to-black/40" />

      {/* Content — bottom-anchored, center-aligned */}
      <div className="relative z-10 flex flex-col items-center justify-end h-full text-center text-white px-6 sm:px-10 pb-12 sm:pb-16">
        {/* Eyebrow */}
        <RichTextContent
          path="hero.eyebrow"
          label="Eyebrow Text"
          defaultValue='<p>Upgrade Your Lifestyle at <span data-type="field-token" data-field="property_name"></span></p>'
          as="div"
          className="hero-entrance hero-entrance-1 font-body text-sm sm:text-base tracking-[0.18em] uppercase mb-3 opacity-90 drop-shadow text-white"
        />

        {/* Main headline */}
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p>Luxury Apartments in <span data-type="field-token" data-field="property_address_city"></span></p>'
          as="div"
          className="hero-entrance hero-entrance-2 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light mb-8 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg text-white"
        />

        {/* Bedroom-type filter buttons — from floorplan_groups, or derived from floorplans */}
        {bedroomGroups.length > 0 ? (
          <div className="hero-entrance hero-entrance-3 flex flex-wrap items-center justify-center gap-3">
            {bedroomGroups.map((group, i) => {
              const beds = canonicalBeds(group.floorplan_group_name);
              const href = beds
                ? floorPlansHref(beds)
                : group.floorplan_group_url || '/floor-plans';
              return (
                <a
                  key={group.floorplan_group_id || i}
                  href={href}
                  className="inline-flex items-center px-7 py-2.5 text-sm font-medium tracking-wide text-white border border-white/75 rounded-sm transition-all duration-200 hover:bg-white hover:text-black hover:border-white"
                >
                  {group.floorplan_group_name || 'View'}
                </a>
              );
            })}
          </div>
        ) : derivedBedButtons.length > 0 ? (
          <div className="hero-entrance hero-entrance-3 flex flex-wrap items-center justify-center gap-3">
            {derivedBedButtons.map((btn) => (
              <a
                key={btn.label}
                href={btn.href}
                className="inline-flex items-center px-7 py-2.5 text-sm font-medium tracking-wide text-white border border-white/75 rounded-sm transition-all duration-200 hover:bg-white hover:text-black hover:border-white"
              >
                {btn.label}
              </a>
            ))}
          </div>
        ) : null}
      </div>

      <style>{`
        /* Vimeo/YouTube iframe — scale to fill and cover like object-fit:cover */
        .hero-video-iframe {
          border: 0;
          pointer-events: none;
          width: 100vw;
          height: 56.25vw; /* 16:9 */
          min-height: 100%;
          min-width: 177.78vh;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.15s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.5s; }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.eyebrow': {
      label: 'Eyebrow Text',
      type: 'richtext',
      defaultValue:
        '<p>Upgrade Your Lifestyle at <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Luxury Apartments in <span data-type="field-token" data-field="property_address_city"></span></p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
  },
  collectionBindings: {
    'hero.bedroom_groups': {
      label: 'Bedroom Type Buttons',
      collection: 'floorplan_groups',
      defaultLimit: 6,
    },
    'hero.floorplans': {
      label: 'Floorplans (fallback source for bedroom buttons)',
      collection: 'floorplans',
      defaultLimit: 50,
    },
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
